import type { SerializedEditorState, SerializedLexicalNode } from 'lexical';

import { clearCommentsBySceneId } from './comments-actions';

import { ApiService } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    addingComment,
    deletedComment,
    deletingComment,
    loadingVideoComments,
    storeComments,
} from 'js/src/data/comments/comments-actions';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch } from 'js/src/libs/RootState';

export function loadVideoComments(video_id: number) {
    return (dispatch: AppDispatch) => {
        dispatch(loadingVideoComments({ status: true }));
        ApiService.videoCommentList({ query: { video_id } })
            .then((response) => {
                const comments = response.data
                    .filter((comment) => comment.slide_id !== null)
                    // sorted chronologically
                    .sort((commentA, commentB) => {
                        return (
                            new Date(commentA.created_at).getTime() -
                            new Date(commentB.created_at).getTime()
                        );
                    });
                dispatch(storeComments({ comments }));
            })
            .catch(ErrorReporter.capture)
            .finally(() => {
                dispatch(loadingVideoComments({ status: false }));
            });
    };
}

/**
 * @typedef {Object} AddVideoCommentArg
 * @property {number} video_id - id of the video comment was posted
 * @property {string} slide_id - scene id of where the comment was posted
 * @property {lexical Obj} message - lexical object of the message of comment
 * @property {Object} notifyUsers - {email: id, email2: id2} that should receive email notification from this comment (mentions)
 * ☝️ this should de-dupe the owner of the video since they will get a notification for every comment posted for the video
 * @property {string} external_user_name - [OPTIONAL] user name of anonymous commenters (non-Lumen5 users)
 * @property {string} external_user_email - [OPTIONAL] email of anonymous commenters (non-Lumen5 users)
 *
 * This method posts the comment to the backend and also iterates through notifyUsers<Array<user_id: Number>>
 * to send notification emails to mentioned users.
 */
export function addVideoComment({
    video_id,
    video_slug,
    slide_id,
    message,
    notifyUsers = {},
    external_user_name = '',
    external_user_email = '',
}: {
    video_id: number;
    video_slug: string;
    slide_id: string;
    message: SerializedEditorState<SerializedLexicalNode>;
    notifyUsers?: Record<string, number | null>;
    external_user_name?: string;
    external_user_email?: string;
}) {
    return (dispatch: AppDispatch) => {
        ApiService.videoCommentCreate({
            body: {
                video_id,
                video_slug,
                slide_id,
                message,
                external_user_name,
                external_user_email,
            },
        })
            .then((response) => {
                const comments = [response.data];
                dispatch(storeComments({ comments }));

                // send email to mentioned users
                Object.keys(notifyUsers).forEach((mentioned_user_email) => {
                    const external_user_name = response.data.external_user_name || '';
                    ApiService.sendSceneCommentMentionEmailCreate({
                        body: {
                            comment_id: response.data.id,
                            commenter_first_name: response.data.user
                                ? response.data.user.first_name
                                : external_user_name,
                            mentioned_user_email,
                            mentioned_user_id: notifyUsers[mentioned_user_email] || null,
                        },
                    })
                        .then(() => {
                            // email sent!
                        })
                        .catch((error: unknown) => {
                            // don't notify user
                            ErrorReporter.capture(error);
                        });
                });
            })
            .catch(ErrorReporter.capture)
            .finally(() => {
                dispatch(addingComment({ status: false }));
            });
    };
}

export function deleteComment({ comment_id }: { comment_id: number }) {
    return (dispatch: AppDispatch) => {
        dispatch(deletingComment({ status: true }));
        ApiService.videoCommentDestroy({ path: { id: comment_id } })
            .then((response) => {
                const { id, slide_id } = response.data;
                dispatch(deletedComment({ comment_id: id, scene_id: slide_id }));
                dispatch(deletingComment({ status: false }));

                window.analytics.track('Deleted Comment from page', {
                    slide_id,
                    comment_id: id,
                    page_url: window.location.href,
                });
            })
            .catch((error) => {
                ErrorReporter.capture(error);
            })
            .finally(() => {
                dispatch(deletingComment({ status: false }));
            });
    };
}

export function resolveReopenComments({
    scene_id,
    comment_id = undefined,
    video_id,
    resolve_comments = false,
}: {
    scene_id: string;
    comment_id?: number;
    video_id: number | null;
    resolve_comments?: boolean;
}) {
    return (dispatch: AppDispatch) => {
        const action = resolve_comments ? 'resolve' : 'reopen';
        if (!video_id) {
            Toast.error(`Failed to ${action} comment, please try again`);
            dispatch(loadingVideoComments({ status: false }));
            return;
        }
        ApiService.resolveCommentsCreate({
            body: {
                scene_id,
                comment_id,
                video_id,
                resolve_comments,
            },
        })
            .then((response) => {
                // clear the comment thread for re-rendering
                dispatch(clearCommentsBySceneId({ scene_id }));

                const comments = response.data
                    .filter((comment) => comment.slide_id !== null)
                    // sorted chronologically
                    .sort((commentA, commentB) => {
                        return (
                            new Date(commentA.created_at).getTime() -
                            new Date(commentB.created_at).getTime()
                        );
                    });
                dispatch(storeComments({ comments }));
            })
            .catch(() => {
                Toast.error(`Failed to ${action} comment, please try again`);
            })
            .finally(() => {
                dispatch(loadingVideoComments({ status: false }));
            });
    };
}
